<template>
  <div class="plan-selection">
    <!-- Loading State -->
    <div v-if="loading" class="text-center py-5">
      <div class="spinner-border text-success" role="status">
        <span class="visually-hidden">Cargando planes...</span>
      </div>
      <p class="text-muted mt-3">Cargando planes disponibles...</p>
    </div>

    <!-- Error State -->
    <div v-else-if="error" class="alert alert-danger" role="alert">
      <i class="fas fa-exclamation-triangle me-2"></i>
      {{ error }}
    </div>

    <!-- Plans Grid -->
    <div v-else class="plans-wrapper">
      <!-- Billing Cycle Toggle -->
      <div class="billing-cycle-toggle">
        <div class="toggle-container">
          <button type="button" class="toggle-option" :class="{ 'active': selectedBillingCycle === 'monthly' }"
            @click="toggleBillingCycle('monthly')">
            <i class="fas fa-calendar-alt me-2"></i>
            Mensual
          </button>
          <button type="button" class="toggle-option" :class="{ 'active': selectedBillingCycle === 'yearly' }"
            @click="toggleBillingCycle('yearly')">
            <i class="fas fa-calendar-check me-2"></i>
            Anual
            <span class="discount-badge">Ahorra 17%</span>
          </button>
        </div>
        <p class="toggle-description">
          <i class="fas fa-info-circle me-1"></i>
          {{ selectedBillingCycle === 'yearly' ? 'Paga 10 meses y obtén 2 meses gratis' : 'Facturación mensual sin compromiso' }}
        </p>
      </div>

      <div v-if="selectedPlanDetails" class="selected-plan-summary">
        <div class="summary-left">
          <span class="summary-label">Plan seleccionado</span>
          <div class="summary-name">
            <i class="fas fa-check-circle"></i>
            {{ selectedPlanDetails.name }}
          </div>
        </div>
        <div class="summary-right">
          <span class="summary-price">
            ${{ formatPrice(getPlanPrice(selectedPlanDetails)) }}
            MXN{{ getPeriodLabel() }}
          </span>
          <span v-if="selectedBillingCycle === 'yearly'" class="summary-discount">
            <i class="fas fa-tag me-1"></i>2 meses gratis
          </span>
          <span class="summary-trial">
            {{ selectedPlanDetails.trial_days }} días de prueba
          </span>
        </div>
      </div>

      <div class="plans-grid">
        <div v-for="plan in plans" :key="plan.code" class="plan-card" :class="{
          'selected': selectedPlan === plan.code,
          'plan-esencial': plan.code === 'esencial',
          'plan-profesional': plan.code === 'profesional',
          'plan-clinica': plan.code === 'clinica'
        }" @click="selectPlan(plan.code)">
          <!-- Selection Indicator -->
          <div v-if="selectedPlan === plan.code" class="selected-badge">
            <i class="fas fa-check-circle"></i>
          </div>

          <!-- Plan Header -->
          <div class="plan-header">
            <h3 class="plan-name">{{ plan.name }}</h3>
            <div class="plan-price">
              <span class="currency">$</span>
              <span class="amount">{{ formatPrice(getPlanPrice(plan)) }}</span>
              <span class="period">MXN{{ getPeriodLabel() }}</span>
            </div>
            <div v-if="selectedBillingCycle === 'yearly'" class="plan-savings">
              <i class="fas fa-tag me-1"></i>
              Ahorra ${{ formatPrice(plan.price_monthly * 2) }} al año
            </div>
          </div>

          <!-- Plan Description -->
          <p class="plan-description">{{ plan.description }}</p>

          <!-- Trial Badge -->
          <div class="trial-badge">
            <i class="fas fa-gift me-2"></i>
            {{ plan.trial_days }} días de prueba gratis
          </div>

          <!-- Usuarios Incluidos -->
          <div class="included-users-info">
            <div class="info-row">
              <i class="fas fa-user-md text-primary"></i>
              <span><strong>{{ plan.included_doctors }}</strong> Médico{{ plan.included_doctors > 1 ? 's' : '' }}
                incluido{{ plan.included_doctors > 1 ? 's' : '' }}</span>
            </div>
            <div class="info-row">
              <i class="fas fa-user-nurse text-success"></i>
              <span><strong>{{ plan.included_assistants }}</strong> Asistente{{ plan.included_assistants > 1 ? 's' : ''
                }}
                incluido{{ plan.included_assistants > 1 ? 's' : '' }}</span>
            </div>
          </div>

          <!-- Roles Permitidos -->
          <div class="roles-info">
            <h6 class="roles-title">
              <i class="fas fa-user-shield me-1"></i>
              Roles Permitidos
            </h6>
            <div class="roles-content">
              <template v-if="plan.code === 'esencial'">
                <div class="role-badge">
                  <i class="fas fa-check-circle"></i>
                  Médico General/Especialista
                </div>
                <div class="role-badge">
                  <i class="fas fa-check-circle"></i>
                  Asistente Médico
                </div>
                <div class="roles-count">3 roles disponibles</div>
              </template>
              <template v-else-if="plan.code === 'profesional'">
                <div class="role-badge">
                  <i class="fas fa-check-circle"></i>
                  Médico General/Especialista
                </div>
                <div class="role-badge">
                  <i class="fas fa-check-circle"></i>
                  Asistente Médico
                </div>
                <div class="role-badge">
                  <i class="fas fa-check-circle"></i>
                  Enfermero(a)
                </div>
                <div class="roles-count">4 roles disponibles</div>
              </template>
              <template v-else-if="plan.code === 'clinica'">
                <div class="all-roles-badge">
                  <i class="fas fa-crown"></i>
                  <strong>Acceso completo a todos los roles</strong>
                </div>
                <div class="roles-count">46 roles disponibles</div>
              </template>
            </div>
          </div>

          <!-- Restricciones y Costos Adicionales -->
          <div class="additional-costs-section">
            <h6 class="costs-title">
              <i class="fas fa-plus-circle me-1"></i>
              Usuarios Adicionales
            </h6>
            <div class="cost-item" :class="{ 'disabled': !plan.can_add_doctors }">
              <div class="cost-label">
                <i class="fas fa-user-md"></i>
                <span>Médico adicional</span>
              </div>
              <div class="cost-value">
                <template v-if="plan.can_add_doctors">
                  <span class="price">${{ formatPrice(plan.price_per_additional_doctor) }}</span>
                  <span class="period">/mes</span>
                </template>
                <template v-else>
                  <span class="not-available">
                    <i class="fas fa-ban"></i>
                    No disponible
                  </span>
                </template>
              </div>
            </div>
            <div class="cost-item">
              <div class="cost-label">
                <i class="fas fa-user-nurse"></i>
                <span>Asistente adicional</span>
              </div>
              <div class="cost-value">
                <span class="price">${{ formatPrice(plan.price_per_additional_assistant) }}</span>
                <span class="period">/mes</span>
              </div>
            </div>
            <div class="cost-item">
              <div class="cost-label">
                <i class="fas fa-user-tie"></i>
                <span>Administrativo adicional</span>
              </div>
              <div class="cost-value">
                <span class="price">${{ formatPrice(plan.price_per_additional_admin) }}</span>
                <span class="period">/mes</span>
              </div>
            </div>
          </div>

          <!-- Modules List -->
          <div class="modules-section">
            <h6 class="modules-title">
              <i class="fas fa-puzzle-piece me-2"></i>
              Módulos Incluidos
            </h6>
            <ul class="modules-list">
              <li v-for="module in plan.modules" :key="module.code" class="module-item">
                <div class="module-icon">
                  <i class="fas fa-check-circle text-success"></i>
                </div>
                <div class="module-info">
                  <span class="module-name">{{ module.name }}</span>
                  <small v-if="module.description" class="module-desc">
                    {{ module.description }}
                  </small>
                  <span v-if="module.config?.is_fixed" class="module-badge fixed">
                    <i class="fas fa-lock"></i> Incluido
                  </span>
                  <span v-else-if="module.config?.optional_price" class="module-badge optional">
                    <i class="fas fa-plus"></i> +${{ formatPrice(module.config.optional_price) }}/mes
                  </span>
                </div>
              </li>
            </ul>
          </div>

          <!-- Select Button -->
          <div class="plan-action">
            <!-- Restricción importante -->
            <div v-if="!plan.can_add_doctors" class="plan-restriction">
              <i class="fas fa-info-circle"></i>
              <span>Este plan no permite agregar más médicos</span>
            </div>
            <div v-else-if="plan.can_add_doctors && plan.code === 'clinica'" class="plan-advantage">
              <i class="fas fa-star"></i>
              <span>¡Mejor precio por médico adicional!</span>
            </div>

            <button type="button" class="btn btn-select"
              :class="selectedPlan === plan.code ? 'btn-success' : 'btn-outline-success'"
              @click.stop="selectPlan(plan.code)">
              <i :class="selectedPlan === plan.code ? 'fas fa-check me-2' : 'fas fa-arrow-right me-2'"></i>
              {{ selectedPlan === plan.code ? 'Plan Seleccionado' : 'Seleccionar Plan' }}
            </button>
          </div>
        </div>
      </div>
    </div>

    <!-- Validation Error -->
    <div v-if="validationError" class="alert alert-danger mt-3" role="alert">
      <i class="fas fa-exclamation-circle me-2"></i>
      {{ validationError }}
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, watch, computed } from 'vue';
import authService from '@/services/auth.service';

const props = defineProps({
  modelValue: {
    type: String,
    default: ''
  },
  billingCycle: {
    type: String,
    default: 'monthly' // monthly o yearly
  },
  validationError: {
    type: String,
    default: null
  }
});

const emit = defineEmits(['update:modelValue', 'update:billingCycle', 'plan-selected']);

const plans = ref([]);
const loading = ref(false);
const error = ref(null);
const selectedPlan = ref(props.modelValue);
const selectedBillingCycle = ref(props.billingCycle);
const selectedPlanDetails = computed(() =>
  plans.value.find((plan) => plan.code === selectedPlan.value)
);

// Precio según ciclo de facturación
const getPlanPrice = (plan) => {
  if (selectedBillingCycle.value === 'yearly') {
    return plan.price_yearly || (plan.price_monthly * 10); // 2 meses gratis
  }
  return plan.price_monthly;
};

const getPeriodLabel = () => {
  return selectedBillingCycle.value === 'yearly' ? '/año' : '/mes';
};

// Watch for external changes to modelValue
watch(() => props.modelValue, (newValue) => {
  selectedPlan.value = newValue;
});

// Watch for external changes to billingCycle
watch(() => props.billingCycle, (newValue) => {
  selectedBillingCycle.value = newValue;
});

onMounted(async () => {
  await loadPlans();
});

const loadPlans = async () => {
  loading.value = true;
  error.value = null;

  try {
    const response = await authService.getPlans();

    // Si response es un array directo
    if (Array.isArray(response)) {
      plans.value = response;
    }
    // Si viene dentro de una propiedad data o plans
    else if (response.data && Array.isArray(response.data)) {
      plans.value = response.data;
    }
    else if (response.plans && Array.isArray(response.plans)) {
      plans.value = response.plans;
    }
    else {
      throw new Error('Formato de respuesta inválido');
    }

    // Filtrar solo planes activos
    plans.value = plans.value.filter(plan => plan.is_active);

    if (plans.value.length === 0) {
      error.value = 'No hay planes disponibles en este momento';
    }
  } catch (err) {
    console.error('Error loading plans:', err);
    error.value = 'Error al cargar los planes disponibles. Por favor, intenta nuevamente.';
  } finally {
    loading.value = false;
  }
};

const toggleBillingCycle = (cycle) => {
  selectedBillingCycle.value = cycle;
  emit('update:billingCycle', cycle);
};

const selectPlan = (planCode) => {
  selectedPlan.value = planCode;
  emit('update:modelValue', planCode);
  emit('plan-selected', planCode);
};

const formatPrice = (price) => {
  return new Intl.NumberFormat('es-MX', {
    minimumFractionDigits: 0,
    maximumFractionDigits: 0
  }).format(price);
};
</script>

<style scoped>
.plan-selection {
  padding: 2rem 0;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 20px;
  margin: -1rem;
  padding: 2rem 2rem 3rem 2rem;
}

.plans-wrapper {
  display: flex;
  flex-direction: column;
  gap: 1.5rem;
  max-width: 1400px;
  margin: 0 auto;
}

.selected-plan-summary {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
  padding: 1rem 1.5rem;
  border-radius: 16px;
  background: linear-gradient(135deg, rgba(45, 206, 137, 0.08), rgba(255, 255, 255, 0.9));
  border: 1px solid rgba(45, 206, 137, 0.2);
  box-shadow: 0 12px 25px rgba(15, 23, 42, 0.06);
}

.summary-left {
  display: flex;
  flex-direction: column;
  gap: 0.35rem;
}

.summary-label {
  font-size: 0.78rem;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
  color: #2dce89;
}

.summary-name {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-weight: 700;
  font-size: 1.1rem;
  color: #1f2937;
}

.summary-name i {
  color: #2dce89;
}

.summary-right {
  display: flex;
  flex-direction: column;
  text-align: right;
  gap: 0.25rem;
}

.summary-price {
  font-weight: 700;
  color: #1f2937;
  font-size: 1rem;
}

.summary-trial {
  font-size: 0.85rem;
  color: #64748b;
}

.plans-grid {
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  gap: 2rem;
  margin-top: 0;
  max-width: 1400px;
  margin-left: auto;
  margin-right: auto;
}

.plan-card {
  position: relative;
  background: white;
  border: 3px solid transparent;
  border-radius: 20px;
  padding: 2rem;
  cursor: pointer;
  transition: all 0.4s cubic-bezier(0.4, 0, 0.2, 1);
  display: flex;
  flex-direction: column;
  min-height: 720px;
  overflow: visible;
  background-clip: padding-box;
}

.plan-card::before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  border-radius: 20px;
  padding: 3px;
  background: linear-gradient(135deg, #e9ecef, #e9ecef);
  -webkit-mask: linear-gradient(#fff 0 0) content-box, linear-gradient(#fff 0 0);
  -webkit-mask-composite: xor;
  mask-composite: exclude;
  transition: all 0.4s ease;
  z-index: -1;
}

.plan-card:hover {
  transform: translateY(-8px) scale(1.02);
  box-shadow: 0 20px 50px rgba(94, 114, 228, 0.2);
}

.plan-card:hover::before {
  background: linear-gradient(135deg, #5e72e4, #2dce89);
}

.plan-card.selected {
  background: linear-gradient(135deg, #f4fdf7 0%, #ffffff 100%);
  box-shadow: 0 25px 60px rgba(45, 206, 137, 0.3);
  transform: translateY(-8px) scale(1.03);
}

.plan-card.selected::before {
  background: linear-gradient(135deg, #2dce89, #2dcecc);
  padding: 3px;
}

/* Plan Color Variants */
.plan-card.plan-esencial.selected {
  background: linear-gradient(135deg, #f0fbff 0%, #ffffff 100%);
}

.plan-card.plan-profesional.selected {
  background: linear-gradient(135deg, #f4f5ff 0%, #ffffff 100%);
}

.plan-card.plan-clinica.selected {
  background: linear-gradient(135deg, #fff4f6 0%, #ffffff 100%);
}

.selected-badge {
  position: absolute;
  top: -12px;
  right: -12px;
  background: linear-gradient(135deg, #2dce89, #2dcecc);
  color: white;
  width: 50px;
  height: 50px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 1.4rem;
  box-shadow: 0 8px 20px rgba(45, 206, 137, 0.5);
  animation: scaleIn 0.4s cubic-bezier(0.68, -0.55, 0.265, 1.55);
  border: 3px solid white;
  z-index: 10;
}

@keyframes scaleIn {
  0% {
    transform: scale(0) rotate(-180deg);
    opacity: 0;
  }

  100% {
    transform: scale(1) rotate(0deg);
    opacity: 1;
  }
}

.plan-header {
  text-align: center;
  margin-bottom: 1.5rem;
  padding-bottom: 1.5rem;
  border-bottom: 2px solid rgba(45, 206, 137, 0.1);
  position: relative;
}

.plan-header::after {
  content: '';
  position: absolute;
  bottom: -2px;
  left: 50%;
  transform: translateX(-50%);
  width: 60px;
  height: 4px;
  background: linear-gradient(90deg, #2dce89, #2dcecc);
  border-radius: 2px;
  opacity: 0;
  transition: opacity 0.3s ease;
}

.plan-card.selected .plan-header::after,
.plan-card:hover .plan-header::after {
  opacity: 1;
}

.plan-name {
  font-size: 1.75rem;
  font-weight: 800;
  color: #32325d;
  margin-bottom: 0.75rem;
  letter-spacing: -0.5px;
}

.plan-price {
  display: flex;
  align-items: baseline;
  justify-content: center;
  margin: 0.5rem 0;
}

.plan-price .currency {
  font-size: 1.2rem;
  font-weight: 600;
  color: #5e72e4;
  margin-right: 0.25rem;
}

.plan-price .amount {
  font-size: 3rem;
  font-weight: 800;
  background: linear-gradient(135deg, #5e72e4, #2dce89);
  -webkit-background-clip: text;
  -webkit-text-fill-color: transparent;
  background-clip: text;
  line-height: 1;
}

.plan-price .period {
  font-size: 0.9rem;
  color: #8898aa;
  margin-left: 0.5rem;
}

.plan-description {
  color: #525f7f;
  font-size: 1rem;
  line-height: 1.6;
  text-align: center;
  margin-bottom: 1.5rem;
  font-weight: 500;
  margin-bottom: 1rem;
}

.trial-badge {
  background: linear-gradient(135deg, #2dce89 0%, #2dcecc 100%);
  color: white;
  padding: 0.75rem 1.25rem;
  border-radius: 50px;
  text-align: center;
  font-weight: 700;
  font-size: 0.9rem;
  margin-bottom: 1.25rem;
  box-shadow: 0 6px 20px rgba(45, 206, 137, 0.35);
  position: relative;
  overflow: hidden;
}

.trial-badge::before {
  content: '';
  position: absolute;
  top: 0;
  left: -100%;
  width: 100%;
  height: 100%;
  background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.3), transparent);
  transition: left 0.5s ease;
}

.plan-card:hover .trial-badge::before {
  left: 100%;
}

.billing-info {
  text-align: center;
  margin-bottom: 1.5rem;
  padding: 0.75rem 1rem;
  background: linear-gradient(135deg, #f8f9fe 0%, #ffffff 100%);
  border-radius: 10px;
  border: 1px solid rgba(94, 114, 228, 0.1);
}

.billing-info small {
  font-weight: 600;
  color: #5e72e4;
}

.included-users-info {
  background: linear-gradient(135deg, #f0f9ff 0%, #ffffff 100%);
  border: 2px solid rgba(45, 206, 137, 0.2);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.info-row {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 0.5rem 0;
  font-size: 0.95rem;
  color: #32325d;
}

.info-row i {
  font-size: 1.2rem;
  width: 24px;
  text-align: center;
}

.info-row strong {
  color: #2dce89;
  font-size: 1.1rem;
}

.roles-info {
  background: linear-gradient(135deg, #fff4f6 0%, #ffffff 100%);
  border: 2px solid rgba(94, 114, 228, 0.2);
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.roles-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #32325d;
  margin-bottom: 0.75rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.roles-content {
  display: flex;
  flex-direction: column;
  gap: 0.5rem;
}

.role-badge {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.5rem 0.75rem;
  background: white;
  border: 1px solid rgba(94, 114, 228, 0.2);
  border-radius: 8px;
  font-size: 0.85rem;
  color: #525f7f;
  transition: all 0.2s ease;
}

.role-badge:hover {
  border-color: #5e72e4;
  background: rgba(94, 114, 228, 0.05);
}

.role-badge i {
  color: #2dce89;
  font-size: 0.9rem;
}

.all-roles-badge {
  display: flex;
  align-items: center;
  gap: 0.75rem;
  padding: 1rem;
  background: linear-gradient(135deg, #fbc713 0%, #f5a623 100%);
  border-radius: 10px;
  color: white;
  font-size: 0.95rem;
  text-align: center;
  justify-content: center;
  box-shadow: 0 4px 15px rgba(251, 199, 19, 0.3);
}

.all-roles-badge i {
  font-size: 1.2rem;
}

.roles-count {
  text-align: center;
  font-size: 0.8rem;
  color: #8898aa;
  font-weight: 600;
  margin-top: 0.5rem;
  padding-top: 0.5rem;
  border-top: 1px solid rgba(0, 0, 0, 0.05);
}

.additional-costs-section {
  background: #f8f9fa;
  border-radius: 12px;
  padding: 1rem;
  margin-bottom: 1.5rem;
}

.costs-title {
  font-size: 0.95rem;
  font-weight: 700;
  color: #32325d;
  margin-bottom: 1rem;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.cost-item {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid rgba(0, 0, 0, 0.05);
  transition: all 0.2s ease;
}

.cost-item:last-child {
  border-bottom: none;
}

.cost-item:hover:not(.disabled) {
  background: rgba(45, 206, 137, 0.05);
  border-radius: 8px;
}

.cost-item.disabled {
  opacity: 0.6;
}

.cost-label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.9rem;
  color: #525f7f;
}

.cost-label i {
  font-size: 1rem;
  width: 20px;
  text-align: center;
  color: #8898aa;
}

.cost-value {
  display: flex;
  align-items: baseline;
  gap: 0.25rem;
}

.cost-value .price {
  font-size: 1.1rem;
  font-weight: 700;
  color: #2dce89;
}

.cost-value .period {
  font-size: 0.8rem;
  color: #8898aa;
}

.cost-value .not-available {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  font-size: 0.85rem;
  font-weight: 600;
  color: #f5365c;
}

.cost-value .not-available i {
  font-size: 1rem;
}

.modules-section {
  flex: 1;
  margin-bottom: 1rem;
  background: #ffffff;
  border-radius: 12px;
  padding: 1rem;
  border: 2px solid rgba(94, 114, 228, 0.1);
}

.modules-title {
  font-size: 1rem;
  font-weight: 700;
  color: #32325d;
  margin-bottom: 1rem;
  padding-bottom: 0.75rem;
  border-bottom: 2px solid rgba(94, 114, 228, 0.1);
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.modules-list {
  list-style: none;
  padding: 0;
  margin: 0;
}

.module-item {
  display: flex;
  align-items: flex-start;
  padding: 0.75rem 0.5rem;
  border-bottom: 1px solid #f7fafc;
  transition: all 0.2s ease;
  border-radius: 8px;
  gap: 0.75rem;
}

.module-item:hover {
  background: rgba(45, 206, 137, 0.05);
  padding-left: 1rem;
}

.module-item:last-child {
  border-bottom: none;
}

.module-icon {
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  flex-shrink: 0;
}

.module-icon i {
  font-size: 1.2rem;
}

.module-item .fa-check {
  color: #2dce89;
  font-weight: 900;
  font-size: 1rem;
}

.module-info {
  display: flex;
  flex-direction: column;
  flex: 1;
  gap: 0.25rem;
}

.module-name {
  color: #32325d;
  font-weight: 600;
  font-size: 0.95rem;
}

.module-desc {
  color: #8898aa;
  font-size: 0.8rem;
  margin-top: 0.15rem;
  display: block;
  line-height: 1.3;
}

.module-badge {
  display: inline-flex;
  align-items: center;
  gap: 0.35rem;
  padding: 0.25rem 0.75rem;
  border-radius: 20px;
  font-size: 0.75rem;
  font-weight: 600;
  margin-top: 0.35rem;
  width: fit-content;
}

.module-badge.fixed {
  background: linear-gradient(135deg, #2dce89 0%, #2dcecc 100%);
  color: white;
}

.module-badge.optional {
  background: linear-gradient(135deg, #f5365c 0%, #f56036 100%);
  color: white;
}

.module-badge i {
  font-size: 0.7rem;
}

.plan-action {
  margin-top: auto;
  padding-top: 1rem;
  border-top: 1px solid #e9ecef;
}

.plan-restriction,
.plan-advantage {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.75rem 1rem;
  border-radius: 10px;
  font-size: 0.85rem;
  font-weight: 600;
  margin-bottom: 1rem;
  text-align: left;
}

.plan-restriction {
  background: linear-gradient(135deg, #fff4f6 0%, #ffffff 100%);
  border: 2px solid rgba(245, 54, 92, 0.2);
  color: #f5365c;
}

.plan-restriction i {
  font-size: 1.1rem;
}

.plan-advantage {
  background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
  border: 2px solid rgba(251, 199, 19, 0.3);
  color: #fbc713;
}

.plan-advantage i {
  font-size: 1.1rem;
}

.btn-select {
  width: 100%;
  padding: 1rem 1.5rem;
  font-weight: 700;
  font-size: 1rem;
  border-radius: 12px;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  border: 2px solid;
  position: relative;
  overflow: hidden;
  z-index: 1;
}

.btn-select::before {
  content: '';
  position: absolute;
  top: 50%;
  left: 50%;
  width: 0;
  height: 0;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.2);
  transform: translate(-50%, -50%);
  transition: width 0.5s ease, height 0.5s ease;
  z-index: -1;
}

.btn-select:hover::before {
  width: 300px;
  height: 300px;
}

.btn-outline-success {
  color: #2dce89;
  border-color: #2dce89;
  background: white;
}

.btn-outline-success:hover {
  background: linear-gradient(135deg, #2dce89 0%, #2dcecc 100%);
  color: white !important;
  transform: translateY(-3px);
  box-shadow: 0 8px 20px rgba(45, 206, 137, 0.4);
  border-color: transparent;
}

.btn-success {
  background: linear-gradient(135deg, #2dce89 0%, #2dcecc 100%);
  border-color: transparent;
  color: white;
  box-shadow: 0 8px 20px rgba(45, 206, 137, 0.4);
}

.btn-success:hover {
  transform: translateY(-3px);
  box-shadow: 0 12px 30px rgba(45, 206, 137, 0.5);
}

/* Responsive */
@media (max-width: 1200px) {
  .plans-grid {
    grid-template-columns: repeat(2, 1fr);
  }
}

@media (max-width: 768px) {
  .selected-plan-summary {
    flex-direction: column;
    align-items: flex-start;
  }

  .summary-right {
    text-align: left;
  }

  .plans-grid {
    grid-template-columns: 1fr;
  }

  .plan-price .amount {
    font-size: 2rem;
  }

  .plan-name {
    font-size: 1.3rem;
  }
}

/* Loading and Error States */
.spinner-border {
  width: 3rem;
  height: 3rem;
}

/* Billing Cycle Toggle Styles */
.billing-cycle-toggle {
  text-align: center;
  margin-bottom: 2rem;
  padding: 1.5rem;
  background: linear-gradient(135deg, #f8f9fa 0%, #ffffff 100%);
  border-radius: 16px;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.05);
}

.toggle-container {
  display: inline-flex;
  background: white;
  border-radius: 12px;
  padding: 0.375rem;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  margin-bottom: 0.75rem;
}

.toggle-option {
  position: relative;
  padding: 0.875rem 2rem;
  border: none;
  background: transparent;
  color: #6c757d;
  font-weight: 600;
  font-size: 0.95rem;
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.3s ease;
  display: flex;
  align-items: center;
  gap: 0.5rem;
}

.toggle-option:hover {
  color: #495057;
}

.toggle-option.active {
  background: linear-gradient(135deg, #2dce89 0%, #2dcecc 100%);
  color: white;
  box-shadow: 0 4px 12px rgba(45, 206, 137, 0.3);
}

.discount-badge {
  display: inline-block;
  background: #ffc107;
  color: #212529;
  padding: 0.25rem 0.625rem;
  border-radius: 12px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
  animation: pulse 2s infinite;
}

@keyframes pulse {

  0%,
  100% {
    transform: scale(1);
  }

  50% {
    transform: scale(1.05);
  }
}

.toggle-description {
  color: #6c757d;
  font-size: 0.875rem;
  margin: 0;
  font-weight: 500;
}

.plan-savings {
  background: linear-gradient(135deg, #fff9e6 0%, #ffffff 100%);
  border: 1px solid rgba(251, 199, 19, 0.3);
  color: #fbc713;
  padding: 0.5rem 1rem;
  border-radius: 8px;
  font-size: 0.875rem;
  font-weight: 600;
  margin-top: 0.75rem;
  display: inline-block;
}

.summary-discount {
  display: inline-block;
  background: #ffc107;
  color: #212529;
  padding: 0.25rem 0.625rem;
  border-radius: 8px;
  font-size: 0.75rem;
  font-weight: 700;
  margin-left: 0.5rem;
}
</style>
